import { axios } from 'src/app/Axios';

import { environment } from 'src/environments';
import { SuccessResponse } from 'src/features/BOM/queries/apiTypes';

import {
    SignUpValidateInvitationResponse,
    InvitedUserSignupRequest,
    NewUserSignupRequest,
    SignInRequest,
    ForgotPasswordRequest,
    ResetPasswordRequest,
    ResetPasswordMailTokenValidationResponse,
    HubspotTokenRequest,
    NewUserSignUpVerifyInvitationResponse,
    NewUserSignupAddCompanyInfoRequest,
    NewUserPersonalInfoSignupRequest,
    SSOAuthenticateRequest,
    TwoFactorAuthLoginRequest,
    TwoFactorPreferencesResponse,
    TwoFactorChangePreferencesRequest,
    RecoveryCodeCountResponse,
    RecoveryCodesResponse,
    SSOSetupRequest
} from './apiTypes';

export const endPoints = {
    signin: '/api/login/',
    forgotPassword: '/api/forgot_password/',
    resetPassword: (token: string) => `/api/reset_password/${token}/`,
    hubspotTokenRequest: `/submittal/hubspot_token_request/`,
    validateInvitedUserSignupInvitation: (token: string) => `/api/validate_invitation/${token}/`,
    signupInvitedUser: '/api/validate_invitation/',
    signupNewUser: '/api/signup/',
    validateNewUserSignupInvitation: (token: string) => `/api/invitation/${token}/`,
    parspecOperatingRegions: '/api/parspec_operating_regions/',
    personalInfo: '/api/personal_info/',
    ssoIdentifier: (url: string) => `${url}/api/login/sso/identifier`,
    ssoAuthenticate: (url: string) => `${url}/api/login/sso/authorize`,
    twoFactorAuthLoginViaAuthenticator: '/api/two_factor/authenticator/login/verify',
    twoFactorAuthLoginViaRecoveryCode: '/api/two_factor/recovery/verify',
    twoFactorPreferences: 'api/two_factor/',
    twoFactorAuthSetupGenerateQRForAuthenticator: 'api/two_factor/authenticator/setup/generate_qr',
    twoFactorAuthVerifyAuthenticatorSetup: 'api/two_factor/authenticator/setup/verify',
    recoveryCodeCountUrl: 'api/two_factor/recovery/',
    generateRecoveryCodeUrl: 'api/two_factor/recovery/generate_codes',
    ssoSetupUrl: 'api/sso/enable',
    disableSSOUrl: 'api/sso/disable'
};

export const postSignInData = async (data: SignInRequest) => {
    return axios.post(endPoints.signin, data);
};
export const postForgotPassword = async (data: ForgotPasswordRequest) => {
    return axios.post(endPoints.forgotPassword, data);
};

export const postResetPassword = async (props: { token: string; data: ResetPasswordRequest }) => {
    return axios.post(endPoints.resetPassword(props.token), props.data);
};

export const getResetPassword = async (token: string) => {
    return axios.get<ResetPasswordMailTokenValidationResponse>(endPoints.resetPassword(token));
};
export const postHubspotTokenRequest = async (data: HubspotTokenRequest) => {
    return axios.post(endPoints.hubspotTokenRequest, data);
};

export const invitedUserSignupTokenValidation = async (token: string) => {
    return axios.get<SignUpValidateInvitationResponse>(endPoints.validateInvitedUserSignupInvitation(token));
};

export const postInvitedUserSignUpData = async (data: InvitedUserSignupRequest) => {
    return axios.post(endPoints.signupInvitedUser, data);
};

export const signupUser = async (data: NewUserSignupRequest) => {
    return axios.post(endPoints.signupNewUser, data);
};

export const newUserSignupTokenValidation = async (token: string) => {
    return axios.get<NewUserSignUpVerifyInvitationResponse>(endPoints.validateNewUserSignupInvitation(token));
};

export const newUserSignupAddCompanyInfo = async (data: NewUserSignupAddCompanyInfoRequest) => {
    return axios.post(endPoints.parspecOperatingRegions, data);
};

export const newUserSignupAddPersonalInfo = async (data: NewUserPersonalInfoSignupRequest) => {
    return axios.post(endPoints.personalInfo, data);
};

export const postSSOIdentifierRequest = async () => {
    return axios.post(endPoints.ssoIdentifier(environment.b));
};

export const postSSOAuthenticationRequest = async (data: SSOAuthenticateRequest) => {
    return axios.post(endPoints.ssoAuthenticate(environment.b), data);
};

export const logOut = () => axios.post<object>('/api/logout/');

export const handleTwoFactorAuthLoginViaAuthenticator = async (data: TwoFactorAuthLoginRequest) => {
    return axios.post(endPoints.twoFactorAuthLoginViaAuthenticator, data);
};

export const handleTwoFactorAuthLoginViaRecoveryCode = async (data: TwoFactorAuthLoginRequest) => {
    return axios.post(endPoints.twoFactorAuthLoginViaRecoveryCode, data);
};

export const getTwoFactorPreferences = async () => {
    return axios.get<TwoFactorPreferencesResponse>(endPoints.twoFactorPreferences);
};

export const updateTwoFactorAuthPreferences = async (data: TwoFactorChangePreferencesRequest) => {
    return axios.patch(endPoints.twoFactorPreferences, data);
};

export const generateQRCodeForAuthenticatorSetup = async () => {
    return axios.post(endPoints.twoFactorAuthSetupGenerateQRForAuthenticator, null, { responseType: 'blob' });
};

export const verifyOTPForAuthenticatorSetup = async (code: string) => {
    return axios.post(endPoints.twoFactorAuthVerifyAuthenticatorSetup, { code });
};

export const getRecoveryCodeCount = async () => {
    return axios.get<RecoveryCodeCountResponse>(endPoints.recoveryCodeCountUrl);
};

export const generateRecoveryCodes = async () => {
    return axios.post<RecoveryCodesResponse>(endPoints.generateRecoveryCodeUrl);
};

export const onSSOSetup = async (input: SSOSetupRequest) => {
    return axios.post<SuccessResponse>(endPoints.ssoSetupUrl, { ...input });
};

export const disableSSOSetup = async () => {
    return axios.post<SuccessResponse>(endPoints.disableSSOUrl);
};
